// What the All brands page says about each brand, from the one read /api/brands answers. Pure: nothing here renders or
// calls the API, so every sentence and figure a row shows is tested without mounting the page. The rules are the pages'
// own: the runway is the Alloy card's, the connection counts are the Connections page's, so a row never disagrees with
// the page it opens.
import { creditUnit, formatUsd, runwayDays } from "@/components/alloy/alloyView";
import { formatRoleLabel } from "@/components/common";

export { formatUsd };

// Fewer days of credit than this at the month's pace is a warning line with Add credits.
export const LOW_CREDITS_DAYS = 7;
// Lines a row shows before "+N more": the rest wait on the brand's own pages.
export const MAX_ROW_ITEMS = 3;
// How many brands one read covers (server/brands.ts MAX_BRANDS).
export const MAX_BRANDS_PER_READ = 25;

const figure = (label, value, sub, tone = "ok") => ({ label, value, sub, tone });
const line = (tone, text, action = null) => ({ tone, text, action });
const fix = (label, page) => ({ label, page });

/** The Alloy card's runway, short enough for a figure: time, not money. */
function runwayShort(days) {
  if (days < 1) return "Less than a day left";
  if (days >= 365) return "More than a year left";
  if (days >= 60) return `≈ ${Math.floor(days / 30)} months left`;
  const whole = Math.floor(days);
  return `≈ ${whole} ${whole === 1 ? "day" : "days"} left`;
}

function creditFigures(brand, now) {
  const { credits, hosting } = brand;
  // A brand profile on its host's billing runs on the host's credits: its own balance is not what its agent spends.
  if (hosting?.billingMode === "host") {
    const host = hosting.hostClientName ?? hosting.hostClientSlug;
    return { credits: figure("Alloy tokens", "Shared", `with ${host}`), month: figure("This month", `In ${host}`, "Billing shared"), items: [], spend: { usd: 0, known: true } };
  }
  if (!credits?.available) {
    return { credits: figure("Alloy tokens", "—", "Could not be read"), month: figure("This month", "—", "Could not be read"), items: [], spend: { usd: 0, known: false } };
  }
  // A brand on Alloy tokens reads its row in tokens, as its Alloy page does. The spend the page head adds stays in
  // dollars and carries the ratio, so the head can read in tokens when every brand that spent does.
  const unit = creditUnit(credits.tokensPerUsd ? { tokens: { perUsd: credits.tokensPerUsd } } : {});
  const Noun = unit.noun.charAt(0).toUpperCase() + unit.noun.slice(1);
  const monthlyUsd = Number(credits.monthlyUsd) || 0;
  const month = figure("This month", unit.labelled(monthlyUsd), "Spent so far");
  const spend = { usd: monthlyUsd, known: true, tokensPerUsd: unit.perUsd };
  if (credits.configured === false) return { credits: figure("Alloy tokens", "Not set up", "Alloy isn't set up yet"), month, items: [], spend };
  const on = credits.alloyState === "on";
  const balance = Number(credits.balanceUsd) || 0;
  const days = runwayDays({ balanceUsd: balance, usage: { monthlyUsd } }, now);
  const items = [];
  let sub;
  let tone = "ok";
  if (!on) sub = "Alloy is off";
  else if (balance <= 0) {
    sub = `No ${unit.noun} left`;
    tone = "bad";
    items.push(line("bad", `Out of ${unit.noun}`, fix(`Add ${unit.noun}`, "alloy")));
  } else if (days === null) sub = "No usage yet this month";
  else {
    sub = runwayShort(days);
    if (days < LOW_CREDITS_DAYS) {
      tone = "warn";
      items.push(line("warn", `${Noun} are running low`, fix(`Add ${unit.noun}`, "alloy")));
    }
  }
  return { credits: figure(unit.tokens ? "Alloy tokens" : "Credits", unit.amount(balance), sub, tone), month, items, spend };
}

function connectionFigures(brand) {
  const counts = brand.connections;
  if (!counts?.available) return { connections: figure("Connections", "—", "Could not be checked"), items: [] };
  const items = (counts.problems ?? []).map((problem) => (problem.status === "not_working"
    ? line("bad", `${problem.label} is not working`, fix("Reconnect", "connections"))
    : line("warn", `${problem.label} has not been tested`, fix("Test it", "connections"))));
  const { total, connected, notWorking, notTested } = counts;
  if (!total) return { connections: figure("Connections", "None yet", "Nothing connected"), items };
  if (notWorking > 0) return { connections: figure("Connections", `${connected} of ${total} working`, `${notWorking} not working`, "bad"), items };
  if (notTested > 0) return { connections: figure("Connections", `${connected} of ${total} working`, `${notTested} not tested`, "warn"), items };
  if (connected === total) return { connections: figure("Connections", "All working", `${total} of ${total}`), items };
  // A key the sweep cannot probe is on the agent, never checked, nothing wrong (the Connections page's "Saved").
  return { connections: figure("Connections", `${connected} of ${total} working`, `${total - connected} saved`), items };
}

/**
 * One brand's line: its agent and state, what needs a person (each with the page that fixes it), and the three figures.
 * `activeSlug` marks the brand the person is in; `now` is the clock the runway is read against.
 */
export function brandRow(brand, { activeSlug = null, now = new Date() } = {}) {
  const agentName = brand.agent?.name || "Your agent";
  const money = creditFigures(brand, now);
  const links = connectionFigures(brand);
  const items = [];
  if (brand.agent?.online === false) items.push(line("bad", `${agentName} is offline`));
  items.push(...links.items, ...money.items);
  const tone = items.some((entry) => entry.tone === "bad") ? "bad" : items.some((entry) => entry.tone === "warn") ? "warn" : "ok";
  return {
    clientSlug: brand.clientSlug,
    clientName: brand.clientName,
    agentName,
    roleLabel: formatRoleLabel(brand.role),
    here: Boolean(activeSlug) && brand.clientSlug === activeSlug,
    tone,
    pill: { tone, label: tone === "ok" ? "Ready" : "Attention" },
    items: items.slice(0, MAX_ROW_ITEMS),
    more: Math.max(0, items.length - MAX_ROW_ITEMS),
    figures: { credits: money.credits, connections: links.connections, month: money.month },
    spend: money.spend,
  };
}

const TONE_RANK = { bad: 0, warn: 1, ok: 2 };

/** The lines that need a person first, the stronger tone before the weaker, each group in the order it was given. */
export function sortByAttention(rows = []) {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => (TONE_RANK[left.row.tone] ?? TONE_RANK.ok) - (TONE_RANK[right.row.tone] ?? TONE_RANK.ok) || left.index - right.index)
    .map(({ row }) => row);
}

/** The page head: how many lines need attention, and what the brands that pay for themselves spent this month. */
export function brandsHeadline(rows = []) {
  let spentUsd = 0;
  let spentComplete = true;
  for (const row of rows) {
    if (row.spend.known) spentUsd += row.spend.usd;
    else spentComplete = false;
  }
  // The head reads in tokens when every brand that spent reads tokens at one ratio (every brand since 2026-11-02); a
  // shared-billing profile adds nothing, so it does not count either way.
  const counted = rows.filter((row) => row.spend.known && row.spend.usd > 0).map((row) => row.spend.tokensPerUsd ?? null);
  const ratios = new Set(counted.length ? counted : rows.map((row) => row.spend.tokensPerUsd ?? null).filter(Boolean));
  const [ratio] = ratios;
  return {
    attention: rows.filter((row) => row.tone !== "ok").length,
    total: rows.length,
    spentUsd: Math.round(spentUsd * 100) / 100,
    spentComplete,
    tokensPerUsd: ratios.size === 1 && ratio ? ratio : null,
  };
}

/** The page head's spend: tokens when the head reads tokens, else dollars. */
export function spentLabel(headline) {
  return headline.tokensPerUsd ? creditUnit({ tokens: { perUsd: headline.tokensPerUsd } }).labelled(headline.spentUsd) : formatUsd(headline.spentUsd);
}
